import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Gamepad2, Plus, Search, UserX } from "lucide-react";
import { cn } from "@v2/lib/utils";
import { Button } from "@v2/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@v2/components/ui/dialog";
import { Input } from "@v2/components/ui/input";
import { Label } from "@v2/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@v2/components/ui/select";
import { supabaseV2 } from "../../lib/supabase";
import { PLAYER_ROLES, playerRoleLabel } from "../../lib/labels";
import { calculateSquadScore } from "../../lib/squad-score";
import { usePermissions } from "../../lib/permissions";
import { isOutOfSquad, isSquadPlayer, playerStatusLabel, groupByRole as groupRowsByRole } from "../../lib/roster";
import { useWorkspaceData } from "../../lib/workspace-data";
import { EmptyState, SectionLoading } from "../../components/state";
import { suggestEaName, type FreeEaName } from "../../lib/ea-suggest";
import { MissingEaPanel } from "./MissingEaPanel";
import type { Player, SquadScoreSetting } from "../../types";
import { matchesPlayedByPlayer } from "../stats/stats-utils";

export function PlayersView({
  teamId,
  hasEaClub = false,
  onOpenPlayer,
}: {
  teamId: string;
  // Squadra con club EA collegato: solo allora ha senso segnalare chi non ha
  // il nome EA (i dati EA restano facoltativi).
  hasEaClub?: boolean;
  onOpenPlayer: (playerId: string) => void;
}) {
  const { t } = useTranslation("players");
  const queryClient = useQueryClient();
  const { canManage } = usePermissions();
  const { players, attendance: responses, matches: allMatches, matchStats, isLoading } = useWorkspaceData(teamId);
  const activeSettingQuery = useQuery({
    queryKey: ["squad-score-settings", teamId, "active"],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("squad_score_settings")
        .select("*")
        .eq("team_id", teamId)
        .eq("is_active", true)
        .order("updated_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data || null) as SquadScoreSetting | null;
    },
    staleTime: 60_000,
  });
  const activeSetting = activeSettingQuery.data;
  const [searchTerm, setSearchTerm] = useState("");
  const [sortMetric, setSortMetric] = useState<"role" | "name" | "goals" | "assists" | "matches" | "starts" | "score">("role");
  const [onlyUnlinked, setOnlyUnlinked] = useState(false);
  const [onlyMissingEa, setOnlyMissingEa] = useState(false);
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");
  const [newEaName, setNewEaName] = useState("");
  const showEaTools = hasEaClub && canManage;
  // Nomi EA del club non ancora agganciati a nessuno (partite + rosa EA).
  const freeEaQuery = useQuery({
    queryKey: ["free-ea-names", teamId],
    enabled: showEaTools,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabaseV2.rpc("list_free_ea_names", { _team_id: teamId });
      if (error) throw error;
      return (data || []) as FreeEaName[];
    },
  });
  const freeEaNames = freeEaQuery.data || [];
  const newPlayerSuggestion = showEaTools && !newEaName.trim()
    ? suggestEaName(freeEaNames, newFirstName, newLastName)
    : null;
  const [createOpen, setCreateOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [createRoleCode, setCreateRoleCode] = useState<string>("");

  const createPlayer = useMutation({
    mutationFn: async (formData: FormData) => {
      const payload = {
        team_id: teamId,
        first_name: String(formData.get("first_name")),
        last_name: String(formData.get("last_name")),
        email: String(formData.get("email") || "") || null,
        shirt_number: formData.get("shirt_number") ? Number(formData.get("shirt_number")) : null,
        role_code: createRoleCode || null,
        // Stesso campo usato da Richieste e dal riquadro "senza EA": e' il
        // nome che compare nelle partite EA.
        ea_account_handle: newEaName.trim() || null,
      };
      const { error } = await supabaseV2.from("players").insert(payload);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["players", teamId] });
      queryClient.invalidateQueries({ queryKey: ["free-ea-names", teamId] });
      queryClient.invalidateQueries({ queryKey: ["players-missing-ea-count"] });
      setCreateOpen(false);
      setCreateRoleCode("");
      setNewFirstName("");
      setNewLastName("");
      setNewEaName("");
    },
  });

  const bulkCreatePlayers = useMutation({
    mutationFn: async (
      rows: Array<{
        first_name: string;
        last_name: string;
        shirt_number?: number | null;
        role_code?: string | null;
        ea_sports_id?: string | null;
      }>
    ) => {
      const payload = rows
        .filter((r) => r.first_name && r.last_name)
        .map((r) => ({
          team_id: teamId,
          first_name: r.first_name,
          last_name: r.last_name,
          shirt_number: r.shirt_number || null,
          role_code: r.role_code || null,
          ea_sports_id: r.ea_sports_id || null,
        }));
      if (payload.length === 0) return;
      const { error } = await supabaseV2.from("players").insert(payload);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["players", teamId] });
      setBulkOpen(false);
    },
  });

  const summaries = useMemo(() => {
    const played = matchesPlayedByPlayer(allMatches, responses, matchStats);
    return players.map((player) => {
      const ps = matchStats.filter((s) => s.player_id === player.id);
      const pr = responses.filter((r) => r.player_id === player.id);
      return {
        player,
        goals: ps.reduce((acc, s) => acc + (s.goals || 0), 0),
        assists: ps.reduce((acc, s) => acc + (s.assists || 0), 0),
        matches: played.get(player.id) || 0,
        starts: ps.filter((s) => s.started).length,
        present: pr.filter((r) => r.status === "present").length,
        late: pr.filter((r) => r.status === "late").length,
        noResponse: pr.filter((r) => r.status === "no_response").length,
        score: activeSetting ? calculateSquadScore(pr, ps, activeSetting) : null,
      };
    });
  }, [players, responses, allMatches, matchStats, activeSetting]);

  const visible = summaries
    .filter((row) => {
      const needle = searchTerm.trim().toLowerCase();
      if (!needle) return true;
      return `${row.player.display_name} ${row.player.role_code || ""} ${row.player.ea_account_handle || ""} ${row.player.ea_sports_id || ""}`
        .toLowerCase()
        .includes(needle);
    })
    .filter((row) => (onlyUnlinked ? !row.player.user_id : true))
    .filter((row) => (onlyMissingEa ? isMissingEa(row.player) : true))
    .sort((a, b) => {
      if (sortMetric === "name" || sortMetric === "role") return a.player.display_name.localeCompare(b.player.display_name);
      if (sortMetric === "assists") return b.assists - a.assists || b.goals - a.goals;
      if (sortMetric === "matches") return b.matches - a.matches || b.goals - a.goals;
      if (sortMetric === "starts") return b.starts - a.starts || b.matches - a.matches;
      if (sortMetric === "score") return (b.score || 0) - (a.score || 0);
      return b.goals - a.goals || b.assists - a.assists;
    });

  const roster = visible.filter((row) => isSquadPlayer(row.player));
  // Fuori rosa: restano visibili (con il loro storico) ma in una sezione
  // separata, cosi' lo staff li ritrova e puo' riattivarli dalla scheda.
  const outOfSquad = visible.filter((row) => isOutOfSquad(row.player));
  const missingEaPlayers = showEaTools
    ? players.filter(isMissingEa).sort((a, b) => a.display_name.localeCompare(b.display_name))
    : [];
  const unlinkedCount = summaries.filter(
    (row) => isSquadPlayer(row.player) && !row.player.user_id,
  ).length;

  return (
    <div className="space-y-6 animate-fade-in">
      <section className="space-y-3">
        <p className="text-muted-foreground">
          {onlyUnlinked
            ? t("summaryFilter", { count: roster.length })
            : unlinkedCount > 0
              ? t("summaryWithUnlinked", { count: roster.length, unlinked: unlinkedCount })
              : t("summary", { count: roster.length })}
        </p>
        <div className="flex flex-wrap gap-2">
          <div className="relative basis-full sm:basis-auto sm:flex-1 sm:max-w-xs">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("search")}
              className="pl-10"
            />
          </div>
          <Button
            variant={onlyUnlinked ? "default" : "outline"}
            onClick={() => setOnlyUnlinked((v) => !v)}
            title={t("unlinkedTitle")}
            className={cn(onlyUnlinked && "bg-amber-600 hover:bg-amber-700")}
          >
            <UserX />
            {t("unlinked")}
            {unlinkedCount > 0 && (
              <span className="inline-flex items-center justify-center rounded-full bg-background/20 px-1.5 text-xs tabular-nums font-semibold">
                {unlinkedCount}
              </span>
            )}
          </Button>
          {showEaTools && (missingEaPlayers.length > 0 || onlyMissingEa) && (
            <Button
              variant={onlyMissingEa ? "default" : "outline"}
              onClick={() => setOnlyMissingEa((v) => !v)}
              title={t("missingEa.filterTitle")}
              className={cn(onlyMissingEa && "bg-amber-600 hover:bg-amber-700")}
            >
              <Gamepad2 />
              {t("missingEa.filter")}
              <span className="inline-flex items-center justify-center rounded-full bg-background/20 px-1.5 text-xs tabular-nums font-semibold">
                {missingEaPlayers.length}
              </span>
            </Button>
          )}
          <Select value={sortMetric} onValueChange={(value) => setSortMetric(value as typeof sortMetric)}>
            <SelectTrigger className="w-auto min-w-[10rem] flex-1 sm:flex-none rounded-full gap-2 px-4">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="role">{t("sort.role")}</SelectItem>
              <SelectItem value="goals">{t("sort.goals")}</SelectItem>
              <SelectItem value="assists">{t("sort.assists")}</SelectItem>
              <SelectItem value="matches">{t("sort.matches")}</SelectItem>
              <SelectItem value="starts">{t("sort.starts")}</SelectItem>
              <SelectItem value="score">{t("sort.score")}</SelectItem>
              <SelectItem value="name">{t("sort.name")}</SelectItem>
            </SelectContent>
          </Select>
          {canManage && (
            <Button variant="outline" onClick={() => setBulkOpen(true)}>
              <Plus />
              {t("addMultiple")}
            </Button>
          )}
          {canManage && (
            <BulkPlayersDialog
              open={bulkOpen}
              onOpenChange={setBulkOpen}
              isSaving={bulkCreatePlayers.isPending}
              onSubmit={(rows) => bulkCreatePlayers.mutate(rows)}
            />
          )}
          <Dialog open={canManage && createOpen} onOpenChange={setCreateOpen}>
            {canManage && (
              <Button onClick={() => setCreateOpen(true)} variant="default">
                <Plus />
                {t("newPlayer")}
              </Button>
            )}
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t("newDialog.title")}</DialogTitle>
                <DialogDescription>{t("newDialog.desc")}</DialogDescription>
              </DialogHeader>
              <form
                className="space-y-3"
                onSubmit={(event) => {
                  event.preventDefault();
                  createPlayer.mutate(new FormData(event.currentTarget));
                }}
              >
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>{t("newDialog.firstName")}</Label>
                    <Input name="first_name" required value={newFirstName} onChange={(e) => setNewFirstName(e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("newDialog.lastName")}</Label>
                    <Input name="last_name" required value={newLastName} onChange={(e) => setNewLastName(e.target.value)} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>{t("newDialog.email")}</Label>
                  <Input name="email" type="email" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label>{t("newDialog.number")}</Label>
                    <Input name="shirt_number" type="number" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("newDialog.role")}</Label>
                    <Select
                      value={createRoleCode || "__none"}
                      onValueChange={(v) => setCreateRoleCode(v === "__none" ? "" : v)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={t("newDialog.selectPh")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">{t("newDialog.roleNone")}</SelectItem>
                        {PLAYER_ROLES.map((r) => (
                          <SelectItem key={r} value={r}>
                            <span className="font-mono mr-2">{r}</span>
                            <span className="text-muted-foreground">{playerRoleLabel(r)}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("newDialog.eaId")}</Label>
                    <Input
                      value={newEaName}
                      onChange={(e) => setNewEaName(e.target.value)}
                      list={showEaTools ? "eb-free-ea-names" : undefined}
                      autoComplete="off"
                    />
                    {showEaTools && (
                      <datalist id="eb-free-ea-names">
                        {freeEaNames.map((n) => (
                          <option key={n.ea_name} value={n.ea_name} />
                        ))}
                      </datalist>
                    )}
                  </div>
                </div>
                {/* Cerca il giocatore tra i nomi EA del club gia' visti nel
                    Mondo FC: se c'e', si collega con un tocco. */}
                {newPlayerSuggestion && (
                  <button
                    type="button"
                    onClick={() => setNewEaName(newPlayerSuggestion.ea_name)}
                    className="w-full text-left rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-primary hover:bg-primary/10"
                  >
                    {t("missingEa.newPlayerFound", {
                      name: newPlayerSuggestion.ea_name,
                      count: newPlayerSuggestion.matches,
                    })}
                  </button>
                )}
                {showEaTools && !newEaName.trim() && !newPlayerSuggestion && (newFirstName || newLastName) && (
                  <p className="text-[11px] text-muted-foreground">{t("missingEa.newPlayerNotFound")}</p>
                )}
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                    {t("newDialog.cancel")}
                  </Button>
                  <Button type="submit" variant="default" disabled={createPlayer.isPending}>
                    {createPlayer.isPending ? t("newDialog.saving") : t("newDialog.create")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </section>

      {!isLoading && unlinkedCount > 0 && (() => {
        const totalRoster = summaries.filter((row) => isSquadPlayer(row.player)).length;
        const linked = totalRoster - unlinkedCount;
        return (
          <div className="rounded-[20px] bg-card px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <div className="flex-1">
              <p className="font-semibold">
                {t("inviteBanner.title", { linked, total: totalRoster })}
              </p>
              <p className="text-[13px] text-muted-foreground mt-0.5">
                {t("inviteBanner.hint")}
              </p>
            </div>
          </div>
        );
      })()}
      {!isLoading && missingEaPlayers.length > 0 && (
        <MissingEaPanel
          teamId={teamId}
          players={missingEaPlayers}
          freeNames={freeEaNames}
          onOpenPlayer={onOpenPlayer}
        />
      )}
      {isLoading ? (
        <SectionLoading />
      ) : (
        <>
          {(roster.length > 0 || outOfSquad.length === 0) && (
            <PlayerCardGrid rows={roster} onOpen={onOpenPlayer} groupByRole={sortMetric === "role"} />
          )}
          {outOfSquad.length > 0 && (
            <section className="space-y-2">
              <div className="px-1">
                <h3 className="text-[17px] font-semibold">{t("grid.outOfSquadTitle", { count: outOfSquad.length })}</h3>
                <p className="text-[13px] text-muted-foreground">{t("grid.outOfSquadHint")}</p>
              </div>
              <PlayerRows rows={outOfSquad} onOpen={onOpenPlayer} />
            </section>
          )}
        </>
      )}
    </div>
  );
}

// Giocatore attivo della rosa senza nome EA: le statistiche del Mondo FC non
// possono agganciarsi.
function isMissingEa(p: Player): boolean {
  return !p.is_guest && p.status === "active" && !p.ea_account_handle && !p.ea_sports_id;
}

type BulkPlayerRow = {
  first_name: string;
  last_name: string;
  shirt_number: string;
  role_code: string;
  ea_sports_id: string;
};

const emptyBulkRow = (): BulkPlayerRow => ({
  first_name: "",
  last_name: "",
  shirt_number: "",
  role_code: "",
  ea_sports_id: "",
});

function BulkPlayersDialog({
  open,
  onOpenChange,
  isSaving,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isSaving: boolean;
  onSubmit: (rows: Array<{
    first_name: string;
    last_name: string;
    shirt_number?: number | null;
    role_code?: string | null;
    ea_sports_id?: string | null;
  }>) => void;
}) {
  const { t } = useTranslation("players");
  const [rows, setRows] = useState<BulkPlayerRow[]>([emptyBulkRow(), emptyBulkRow(), emptyBulkRow()]);

  const updateRow = (index: number, field: keyof BulkPlayerRow, value: string) => {
    setRows((current) => current.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const valid = rows.filter((r) => r.first_name.trim() && r.last_name.trim());

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{t("bulkDialog.title")}</DialogTitle>
          <DialogDescription>{t("bulkDialog.desc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-[60vh] overflow-y-auto">
          <div className="grid grid-cols-12 gap-2 text-xs font-semibold uppercase text-muted-foreground tracking-wider px-1">
            <div className="col-span-3">{t("bulkDialog.headFirst")}</div>
            <div className="col-span-3">{t("bulkDialog.headLast")}</div>
            <div className="col-span-1">{t("bulkDialog.headNumber")}</div>
            <div className="col-span-2">{t("bulkDialog.headRole")}</div>
            <div className="col-span-3">{t("bulkDialog.headEa")}</div>
          </div>
          {rows.map((row, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2">
              <Input
                className="col-span-3"
                value={row.first_name}
                onChange={(e) => updateRow(idx, "first_name", e.target.value)}
                placeholder={t("bulkDialog.firstPh")}
              />
              <Input
                className="col-span-3"
                value={row.last_name}
                onChange={(e) => updateRow(idx, "last_name", e.target.value)}
                placeholder={t("bulkDialog.lastPh")}
              />
              <Input
                className="col-span-1"
                value={row.shirt_number}
                onChange={(e) => updateRow(idx, "shirt_number", e.target.value)}
                type="number"
                placeholder="#"
              />
              <div className="col-span-2">
                <Select
                  value={row.role_code || "__none"}
                  onValueChange={(v) => updateRow(idx, "role_code", v === "__none" ? "" : v)}
                >
                  <SelectTrigger className="text-xs">
                    <SelectValue placeholder={t("bulkDialog.rolePh")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">-</SelectItem>
                    {PLAYER_ROLES.map((r) => (
                      <SelectItem key={r} value={r} className="text-xs">
                        <span className="font-mono mr-1.5">{r}</span>
                        <span className="text-muted-foreground">{playerRoleLabel(r)}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Input
                className="col-span-3"
                value={row.ea_sports_id}
                onChange={(e) => updateRow(idx, "ea_sports_id", e.target.value)}
                placeholder={t("bulkDialog.headEa")}
              />
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setRows((c) => [...c, emptyBulkRow()])}>
            <Plus />
            {t("bulkDialog.addRow")}
          </Button>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("bulkDialog.cancel")}
          </Button>
          <Button
            disabled={isSaving || valid.length === 0}
            onClick={() =>
              onSubmit(
                valid.map((r) => ({
                  first_name: r.first_name.trim(),
                  last_name: r.last_name.trim(),
                  shirt_number: r.shirt_number ? Number(r.shirt_number) : null,
                  role_code: r.role_code.trim() || null,
                  ea_sports_id: r.ea_sports_id.trim() || null,
                }))
              )
            }
          >
            {isSaving ? t("bulkDialog.saving") : t("bulkDialog.submit", { count: valid.length })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type PlayerSummary = {
  player: Player;
  goals: number;
  assists: number;
  matches: number;
  starts: number;
  present: number;
  late: number;
  noResponse: number;
  score: number | null;
};

function PlayerCardGrid({ rows, onOpen, groupByRole }: { rows: PlayerSummary[]; onOpen: (playerId: string) => void; groupByRole: boolean }) {
  const { t } = useTranslation("players");
  if (rows.length === 0) {
    return (
      <div className="rounded-[20px] bg-card p-6 flex flex-col items-center text-center gap-2">
        <p className="text-[17px] font-semibold">{t("emptyHero.title")}</p>
        <p className="text-muted-foreground max-w-md">{t("emptyHero.subtitle")}</p>
      </div>
    );
  }
  if (!groupByRole) return <PlayerRows rows={rows} onOpen={onOpen} wide />;
  const groups = groupRowsByRole(rows, (r) => r.player.role_code);
  return (
    <div className="grid xl:grid-cols-2 gap-x-4 gap-y-5 items-start">
      {groups.map((group) => (
        <section key={group.key} className="space-y-2">
          <h3 className="px-1 flex items-baseline gap-2 text-[17px] font-semibold">
            {t(`grid.groups.${group.key}`)}
            <span className="text-[13px] font-normal text-[hsl(var(--faint))] tabular-nums">{group.rows.length}</span>
          </h3>
          <PlayerRows rows={group.rows} onOpen={onOpen} />
        </section>
      ))}
    </div>
  );
}

function PlayerRows({ rows, onOpen, wide = false }: { rows: PlayerSummary[]; onOpen: (playerId: string) => void; wide?: boolean }) {
  return (
    <div className={cn("rounded-[20px] bg-card overflow-hidden", wide && "xl:grid xl:grid-cols-2 xl:gap-x-px")}>
      {rows.map((row) => (
        <PlayerCard key={row.player.id} row={row} onOpen={() => onOpen(row.player.id)} />
      ))}
    </div>
  );
}

function PlayerCard({ row, onOpen }: { row: PlayerSummary; onOpen: () => void }) {
  const { t } = useTranslation("players");
  const { player } = row;
  const showAccountState = !player.is_guest;
  const linked = !!player.user_id;
  const outOfSquad = isOutOfSquad(player);
  return (
    <button
      onClick={onOpen}
      className={cn("w-full text-left px-4 py-3 border-t first:border-t-0 hover:bg-raised transition-colors flex gap-3", outOfSquad && "opacity-70")}
    >
      <span className={cn("eb-num text-[30px] w-10 shrink-0 text-center pr-1", player.shirt_number == null && "text-[hsl(var(--faint))]")}>
        {player.shirt_number ?? "-"}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-start justify-between gap-3">
          <span className="min-w-0">
            <span className="flex items-center gap-1.5 min-w-0">
              <span className="font-semibold truncate">{player.display_name}</span>
              {showAccountState && linked && <span aria-label={t("grid.accountLinked")} title={t("grid.accountLinked")} className="h-2 w-2 rounded-full bg-[hsl(var(--ok))] shrink-0" />}
              {(player.is_guest || outOfSquad) && (
                <span className="rounded-full bg-raised px-2 text-xs text-muted-foreground shrink-0">
                  {player.is_guest ? t("grid.guest") : playerStatusLabel(player.status)}
                </span>
              )}
            </span>
            <span className="block text-[13px] text-[hsl(var(--faint))] truncate">
              {player.role_code || t("grid.roleNa")} · {player.ea_account_handle || player.ea_sports_id || t("grid.eaNa")}
            </span>
          </span>
          <span className="grid grid-cols-5 gap-2 shrink-0 text-center">
            <StatCell label="PT" value={row.matches} />
            <StatCell label="T" value={row.starts} />
            <StatCell label="G" value={row.goals} />
            <StatCell label="A" value={row.assists} />
            <StatCell label="P" value={row.present} accent={row.present > 0 ? "text-[hsl(var(--ok))]" : undefined} />
          </span>
        </span>
        <span className="mt-1 flex flex-wrap items-center justify-between gap-x-3 text-[13px] text-[hsl(var(--faint))]">
          <span>{t("grid.lateAndNoResponse", { late: row.late, noResponse: row.noResponse })}</span>
          {showAccountState && !linked ? (
            <span className="inline-flex items-center gap-1.5 text-[hsl(var(--warn))]">
              <span aria-hidden className="h-2 w-2 rounded-[2px] bg-[hsl(var(--warn))]" />
              {t("grid.accountPending")}
            </span>
          ) : (
            <span>{t("grid.totalMatches", { count: row.matches })}</span>
          )}
        </span>
      </span>
    </button>
  );
}

function StatCell({ label, value, accent }: { label: string; value: number | string; accent?: string }) {
  return (
    <span className="block min-w-[20px]">
      <span className={cn("eb-num block text-[20px] pr-0.5", accent)}>{value}</span>
      <span className="block text-[11px] leading-tight text-[hsl(var(--faint))]">{label}</span>
    </span>
  );
}
